import PropTypes from 'prop-types';
import { BsBuilding, BsGeoAlt, BsPeople, BsCurrencyEuro, BsCalendar3 } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Tag from '#globalComponents/Tag/Tag';

import { useTargetingVocabulary } from '../../models/targeting/queries';
import { formatEurCompact } from '../../utils/formatters';
import { FACET_LABELS, facetValueLabel } from '../../utils/targetingLabels';
import CompanyKindBadge from '../CompanyKind/CompanyKindBadge';
import PreviewNotes from '../CompanyKind/PreviewNotes';

const HIGHLIGHT_PART = /(<b>.*?<\/b>)/g;

// The backend marks full-text hits with <b>; everything is rendered as text, never as HTML.
const SnippetText = ({ snippet }) => (
	<p className="text-xs italic text-bgBase-800 leading-snug">
		{snippet
			.split(HIGHLIGHT_PART)
			.filter(Boolean)
			.map((part, index) =>
				part.startsWith('<b>') && part.endsWith('</b>') ? (
					<strong key={index} className="font-semibold text-text">
						{part.slice(3, -4)}
					</strong>
				) : (
					part
				),
			)}
	</p>
);

SnippetText.propTypes = { snippet: PropTypes.string.isRequired };

const ProfileEvidence = ({ profile, vocabulary }) => (
	<div className="mt-2 space-y-1.5">
		{profile.oneLiner && <p className="text-xs text-text leading-snug">{profile.oneLiner}</p>}
		{profile.matched?.length > 0 && (
			<div className="flex flex-wrap gap-1">
				{profile.matched.slice(0, 3).map((match) => (
					<Tag
						key={`${match.facet}:${match.value}`}
						prefix={FACET_LABELS[match.facet] ?? match.facet}
						label={facetValueLabel(vocabulary, match.facet, match.value)}
						title={match.evidence ? `„${match.evidence}“` : undefined}
						tone="success"
						size="xs"
					/>
				))}
			</div>
		)}
		{profile.snippet && <SnippetText snippet={profile.snippet} />}
	</div>
);

ProfileEvidence.propTypes = {
	profile: PropTypes.shape({
		oneLiner: PropTypes.string,
		matched: PropTypes.arrayOf(
			PropTypes.shape({ facet: PropTypes.string, value: PropTypes.string, evidence: PropTypes.string }),
		),
		snippet: PropTypes.string,
	}).isRequired,
	vocabulary: PropTypes.object,
};

const TotalLine = ({ counts }) => {
	const isEstimate = counts.stages?.some((stage) => stage.estimate);
	return (
		<p className="text-xs font-semibold text-text">
			Treffer gesamt: {isEstimate ? 'ca. ' : ''}
			{Math.round(counts.total).toLocaleString('de-DE')}
		</p>
	);
};

TotalLine.propTypes = {
	counts: PropTypes.shape({ total: PropTypes.number.isRequired, stages: PropTypes.array }).isRequired,
};

/**
 * Single preview company card.
 */
const PreviewCard = ({ company, vocabulary }) => {
	const category = Array.isArray(company.companyCategory) ? company.companyCategory[0] : company.companyCategory;

	const details = [
		category && { icon: BsBuilding, text: category, title: 'Branche' },
		company.addressCity && { icon: BsGeoAlt, text: company.addressCity, title: 'Standort' },
		company.employeeCount !== null &&
			company.employeeCount !== undefined && {
				icon: BsPeople,
				text: String(company.employeeCount),
				title: 'Mitarbeiter',
			},
		company.revenue !== null &&
			company.revenue !== undefined && {
				icon: BsCurrencyEuro,
				text: formatEurCompact(company.revenue),
				title: 'Umsatz',
			},
		company.foundingYear && { icon: BsCalendar3, text: String(company.foundingYear), title: 'Gegründet' },
	].filter(Boolean);

	return (
		<div className="p-3 bg-white border border-gray-200 rounded-lg">
			<div className="mb-1.5 flex flex-wrap items-center gap-1.5">
				<p className="text-sm font-semibold text-text">{company.companyName || 'Unbekannt'}</p>
				<CompanyKindBadge company={company} vocabulary={vocabulary} size="xs" />
			</div>
			<div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-bgBase-800">
				{details.map((d, i) => (
					<span key={i} className="flex items-center gap-1" title={d.title}>
						<d.icon className="w-3 h-3 flex-shrink-0" />
						{d.text}
					</span>
				))}
			</div>
			{company.websiteProfile && <ProfileEvidence profile={company.websiteProfile} vocabulary={vocabulary} />}
		</div>
	);
};

/**
 * Preview of a chat search: total count with its notes (too-broad full text, hidden holdings),
 * sample companies with the evidence behind each match, then the approve/refine buttons.
 */
const AgenticPreviewCards = ({
	companies,
	counts,
	companyKinds,
	onCompanyKindsChange,
	onApprove,
	onRefine,
	isExecuting,
}) => {
	const { data: vocabulary } = useTargetingVocabulary();
	const notes = (
		<PreviewNotes
			hiddenByCompanyKind={counts?.hiddenByCompanyKind}
			warnings={counts?.warnings}
			companyKinds={companyKinds}
			onCompanyKindsChange={onCompanyKindsChange}
		/>
	);

	if (!companies || companies.length === 0) {
		return (
			<div className="space-y-2 ml-8">
				<div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-caution-700">
					Keine Unternehmen gefunden. Versuchen Sie, Ihre Kriterien anzupassen.
				</div>
				{notes}
			</div>
		);
	}

	return (
		<div className="space-y-2 ml-8">
			{typeof counts?.total === 'number' && <TotalLine counts={counts} />}
			{notes}
			{companies.map((company) => (
				<PreviewCard key={company.id} company={company} vocabulary={vocabulary} />
			))}
			<div className="flex gap-2 pt-1">
				<Button onClick={onApprove} size="sm" className="flex-1" loading={isExecuting}>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="w-3.5 h-3.5"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M7 10v12" />
						<path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
					</svg>
					{isExecuting ? 'Wird gestartet...' : 'Ja, passt \u2013 alle laden'}
				</Button>
				<Button onClick={onRefine} disabled={isExecuting} variant="outline" size="sm" className="flex-1">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="w-3.5 h-3.5"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M17 14V2" />
						<path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
					</svg>
					Nein, anpassen
				</Button>
			</div>
		</div>
	);
};

PreviewCard.propTypes = {
	company: PropTypes.shape({
		id: PropTypes.string,
		companyName: PropTypes.string,
		companyCategory: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
		addressCity: PropTypes.string,
		employeeCount: PropTypes.number,
		revenue: PropTypes.number,
		foundingYear: PropTypes.number,
		websiteProfile: PropTypes.object,
		isOperating: PropTypes.bool,
		nonOperatingRole: PropTypes.string,
		operatingCompanyName: PropTypes.string,
	}).isRequired,
	vocabulary: PropTypes.object,
};

AgenticPreviewCards.propTypes = {
	companies: PropTypes.array.isRequired,
	counts: PropTypes.shape({
		total: PropTypes.number,
		stages: PropTypes.array,
		hiddenByCompanyKind: PropTypes.number,
		warnings: PropTypes.array,
	}),
	companyKinds: PropTypes.arrayOf(PropTypes.string),
	onCompanyKindsChange: PropTypes.func,
	onApprove: PropTypes.func.isRequired,
	onRefine: PropTypes.func.isRequired,
	isExecuting: PropTypes.bool,
};

export default AgenticPreviewCards;
